import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import * as R from 'ramda';

import { GeoJSONGeometry, LocationGeometry } from '@atom/types/geojson';

import { getGoogleShapeCenter } from '../mapUtilities';

import {
  DrawingConfig,
  EditingConfig,
  EditTargetView,
  MapDrawing,
  MapDrawingSlice,
  MapEditingSlice,
} from './drawingTypes';
import {
  EMPTY_FEATURES,
  Interaction,
  toFeature,
  toFeatureCollection,
} from './geometry';

const useMapDrawing = (args: {
  editing?: EditingConfig;
  drawing?: DrawingConfig;
}): MapDrawing => {
  const { editing, drawing } = args;
  const { targets, onSave } = editing ?? {};

  const autoEdit = Boolean(editing?.autoEdit && editing.targets.length === 1);
  const autoCommit = Boolean(editing?.autoCommit);
  const lockedMode = editing?.lockMode ?? null;

  // The single live shape. When autoEdit is on, seed from the sole target's
  // existing point geometry so a pre-seeded point is immediately visible.
  const [liveFeature, setLiveFeature] = useState<Feature | null>(() =>
    autoEdit && editing?.targets[0]?.location
      ? toFeature(editing.targets[0].location)
      : null,
  );
  const [drawingMode, setDrawingMode] = useState<Interaction>('select');
  const [selectedId, setSelectedId] = useState<string>(
    editing?.targets.length === 1 ? editing.targets[0].id : '',
  );
  const [activeId, setActiveId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(autoEdit);
  // Just-saved feature, used so the location doesn't flicker during the async
  // save while the source data catches up.
  const [committed, setCommitted] = useState<Feature | null>(null);

  useEffect(() => {
    setCommitted(null);
  }, [selectedId]);

  const selected = targets?.find(target => target.id === selectedId) ?? null;

  // Drop the committed geometry once the source data matches it,
  // so a later location change on the still-selected target isn't frozen out.
  useEffect(() => {
    if (
      committed &&
      selected &&
      R.equals(selected.location, committed.geometry as GeoJSONGeometry)
    ) {
      setCommitted(null);
    }
  }, [committed, selected]);

  const autoEditPointLocation =
    autoEdit && lockedMode === 'point'
      ? (editing?.targets[0]?.location ?? null)
      : null;

  useEffect(() => {
    if (!(autoEdit && lockedMode === 'point')) {
      return;
    }

    setLiveFeature(
      autoEditPointLocation ? toFeature(autoEditPointLocation) : null,
    );
  }, [autoEdit, lockedMode, autoEditPointLocation]);

  const autoEditLineLocation =
    autoEdit && lockedMode === 'linestring'
      ? (editing?.targets[0]?.location ?? null)
      : null;

  // Line autoCommit editors commit geometry to `committed` and leave edit mode, so
  // a committed line outlives a parent that clears `location` (the committed/source
  // mismatch keeps it on screen). When the sole target's location is cleared
  // externally, drop the committed/live shape and re-enter the empty draw state —
  // this is the no-remount way to clear, preserving the map camera.
  useEffect(() => {
    if (!(autoEdit && lockedMode === 'linestring')) {
      return;
    }

    if (autoEditLineLocation === null) {
      setCommitted(null);
      setLiveFeature(null);
      setIsEditing(true);
    }
  }, [autoEdit, lockedMode, autoEditLineLocation]);

  const handleSelect = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const handleEdit = useCallback(
    (id: string) => {
      setActiveId(id);
      const target = targets?.find(item => item.id === id) ?? null;
      // Seed the target's existing geometry (point or line)
      const seed =
        (id === selectedId ? committed : null) ??
        (target?.location ? toFeature(target.location) : null);

      setSelectedId(id);
      setLiveFeature(seed);
      setIsEditing(true);
    },
    [targets, selectedId, committed],
  );

  const handleSave = useCallback(() => {
    if (liveFeature && onSave) {
      onSave(liveFeature.geometry as LocationGeometry, selectedId);
      setCommitted(liveFeature);
    }

    setIsEditing(false);
    setLiveFeature(null);
    setActiveId('');
  }, [liveFeature, selectedId, onSave]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setLiveFeature(null);
    setActiveId('');
  }, []);

  // Clear the current shape but stay in edit mode
  const handleClear = useCallback(() => {
    setLiveFeature(null);
    setCommitted(null);
    editing?.onChange?.(null);
  }, [editing]);

  const handleChange = useCallback(
    (next: FeatureCollection) => {
      const feature = (next.features[0] as Feature) ?? null;
      drawing?.onChange?.(feature);
      editing?.onChange?.(
        feature ? (feature.geometry as LocationGeometry) : null,
      );

      // Line autoCommit is one-shot: finishing the draw commits the geometry and
      // leaves edit mode so the target renders statically (line + center pin)
      // rather than staying editable. Re-drawing happens via a reset/remount.
      if (autoCommit && lockedMode === 'linestring' && feature) {
        setCommitted(feature);
        setIsEditing(false);
        setLiveFeature(null);
        setActiveId('');
        return;
      }

      setLiveFeature(feature);
    },
    [drawing, editing, autoCommit, lockedMode],
  );

  const handleModeChange = useCallback(
    (next: Interaction) => {
      // Switching tools always starts fresh, so the previous live shape clears.
      setDrawingMode(next);
      setLiveFeature(null);
      drawing?.onChange?.(null);
      drawing?.onModeChange?.(next);
    },
    [drawing],
  );

  const handleClose = useCallback(() => {
    if (liveFeature === null) {
      setDrawingMode('select');
    } else {
      setLiveFeature(null);
      drawing?.onChange?.(null);
    }
  }, [liveFeature, drawing]);

  const targetViews: EditTargetView[] = useMemo(
    () =>
      (targets ?? []).map(target => {
        const geometry =
          target.id === selectedId && committed
            ? (committed.geometry as GeoJSONGeometry)
            : target.location;

        return {
          id: target.id,
          name: target.name,
          icon: target.listIcon ?? target.icon,
          mapIcon: target.icon,
          mapActiveIcon: target.activeIcon ?? target.icon,
          type: target.type,
          geometry,
          color: target.color,
          isSelected: target.id === selectedId,
          isActive: target.id === activeId,
          address: target.address,
          coordinates: geometry ? getGoogleShapeCenter(geometry) : null,
          canEdit: target.canEdit,
        };
      }),
    [targets, selectedId, activeId, committed],
  );

  const selectedEditMode: 'point' | 'linestring' =
    lockedMode ??
    (selected?.location?.type === 'LineString' ? 'linestring' : 'point');

  const mode = isEditing ? selectedEditMode : drawingMode;

  const features = useMemo<FeatureCollection>(
    () =>
      liveFeature === null ? EMPTY_FEATURES : toFeatureCollection(liveFeature),
    [liveFeature],
  );

  const editingSlice = useMemo<MapEditingSlice | null>(
    () =>
      editing
        ? {
            targetViews,
            isEditing,
            hasDraft: Boolean(liveFeature),
            onSelect: handleSelect,
            onEdit: handleEdit,
            onSave: handleSave,
            onCancel: handleCancel,
            onClear: handleClear,
            editMode: selectedEditMode,
            selected,
            draft: liveFeature,
            committed,
          }
        : null,
    [
      editing,
      targetViews,
      isEditing,
      liveFeature,
      handleSelect,
      handleEdit,
      handleSave,
      handleCancel,
      handleClear,
      selectedEditMode,
      selected,
      committed,
    ],
  );

  const drawingSlice = useMemo<MapDrawingSlice | null>(
    () =>
      drawing
        ? {
            mode: drawingMode,
            onModeChange: handleModeChange,
            activeFeature: liveFeature,
            onClose: handleClose,
            stats: drawing.stats,
          }
        : null,
    [drawing, drawingMode, handleModeChange, liveFeature, handleClose],
  );

  return {
    features,
    mode,
    onChange: handleChange,
    onGrab: drawing?.onGrab,
    editingSlice,
    drawingSlice,
  };
};

export default useMapDrawing;
